import {
  Control,
  Controls,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { ControlStatusItem } from '@trustero/trustero-api-web/lib/model/control_status_pb'
import { Department } from '@trustero/trustero-api-web/lib/model/department_pb'
import {
  PolicyRecord,
  PolicyRecords,
} from '@trustero/trustero-api-web/lib/model/policy_pb'
import { ComplianceFrameworks } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import {
  Framework,
  Frameworks,
} from '@trustero/trustero-api-web/lib/model/framework_pb'
import FileSaver from 'file-saver'
import Papa from 'papaparse'
import {
  AuditReadinessRecord,
  AuditReadinessTestRecord,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'
import {
  getAdvancedText,
  getControlTestName,
  getDateText,
  getReasonText,
  getSingleCheckResultText,
} from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.helpers'
import { ResponsibilityOptions } from 'src/components/PageLayout/ShowPage/Dropdowns/Dropdowns.constants'
import { CONTROL_TEST_NAMES } from 'src/pages/AuditBot/accordion/subsection/ControlChecks/AuditBotControlChecks.constants'
import { VendorRecord } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import {
  OeControlTestCitation,
  OeControlTestCitationType,
  OeControlTestDetermination,
  OeControlTestResult,
} from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import dayjs from 'dayjs'
import { generatePermalink } from 'src/components/PageLayout/Permalink/generatePermalink'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { controlStatusTitle } from '../../lib/common/types'
import { NONE_NAME, NO_REQUIREMENTS_INDICATOR } from '../globalConstants'
import { getFrameworkGroups } from '../globalHelpers'
import { ComplianceFrameworkGroup } from '../globalTypes'
import { exportToXlsx } from './xlsxExport'

const CONTROLS_CSV_HEADERS = [
  'Control ID',
  'Control Name',
  'Objective',
  'Notes',
  'Procedure',
  'Policy',
  'Department',
  'Status',
  'Responsibility',
  'Reason',
  'Vendor',
  'Owner',
  'Assignees',
  'Required Evidence',
  'Test Procedures',
  'Risks',
]

// Link columns, emitted only when the caller supplies a page context to build links from; a link
// column of empty cells is worse than none. Safe to add to a re-importable export: the importer
// declares them optional and prepareControlsUpload never reads them, the same way the framework
// and control-test columns ride along.
const CONTROL_URL_HEADER = 'Control URL'

// The control link sits next to the control it names rather than at the end of a wide row.
const CONTROL_URL_INDEX = CONTROLS_CSV_HEADERS.indexOf('Control Name') + 1

// Header and body rows have to be spliced the same way, so both go through here.
const withControlUrl = (cells: string[], url?: string): string[] =>
  url
    ? [
        ...cells.slice(0, CONTROL_URL_INDEX),
        url,
        ...cells.slice(CONTROL_URL_INDEX),
      ]
    : cells

const nameResult = (name: string) => `${getControlTestName(name)} - Result`
const nameReason = (name: string) => `${getControlTestName(name)} - Reason`
const nameDate = (name: string) =>
  `${getControlTestName(name)} - Date Performed`
const nameAdvanced = (name: string) =>
  `${getControlTestName(name)} - Using Advanced Control Test`
const nameDetails = (name: string) => `${getControlTestName(name)} - Details`
// Links to the run that produced the result — the control test show page.
const nameUrl = (name: string) => `${getControlTestName(name)} - URL`

const quoteRequirementForList = (value: string): string => {
  const escaped = value.replace(/"/g, '""')
  return `"${escaped}"`
}

// Excel formula-parses any cell whose text starts with = + - @ (or a leading
// tab/CR), so a markdown bullet like "- Screenshot of X" renders as #NAME?.
// CSV quoting does NOT prevent this — the trigger has to stop being the first character.
// A leading space is the least invasive guard that works; Excel keeps the space
// but does not evaluate the cell. stripFormulaGuard takes it back off on
// import, which keeps this export re-importable — keep the two in sync.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/
const guardFormulaCell = (value: string): string =>
  FORMULA_TRIGGER.test(value) ? ` ${value}` : value

// Test procedures from the new model's repeated test_procedures on the Control,
// formatted to match the backend's ReconstructTestProceduresText.
const formatTestProcedures = (control: Control): string =>
  (control.getSuggestions()?.getTestProceduresList() ?? [])
    .map((tp) => {
      const title = tp.getTitle().trim()
      const body = tp.getBody().trim()
      return title ? `- **${title}:** ${body}` : body
    })
    .filter((line) => line.length > 0)
    .join('\n')

// Suggested/required evidence from the new model (batch-loaded onto the Control).
const formatSuggestedEvidence = (control: Control): string =>
  control.getSuggestions()?.getSuggestedEvidence()?.getBody()?.trim() ?? ''

const showPageLink = (
  pageContext: string,
  modelType: MODEL_TYPE,
  modelId: string,
): string =>
  generatePermalink({
    pageContext,
    modelType,
    modelId,
    // Absolute: the csv is read outside the app, where a relative path means nothing.
    isInternalLink: false,
  })

// One cited source: its caption, when it was relevant, a link to it, and what the check made of it.
//
// Evidence absent from evidenceLinkIds has been deleted since the test ran and gets no link — the
// server omits exactly those, matching the audit-scan UI, which renders them "(Deleted)".
const formatCitation = (
  citation: OeControlTestCitation.AsObject,
  pageContext: string,
  evidenceLinkIds: Map<string, string>,
): string => {
  const isContext = citation.citationType === OeControlTestCitationType.CONTEXT
  const title = isContext ? 'Organizational Context' : citation.evidenceCaption
  const formattedDate = citation.relevantDate
    ? dayjs(citation.relevantDate).format(DATE_FORMATS.ISO_WITH_TIME)
    : ''

  const lines = [formattedDate ? `${title}  ${formattedDate}` : title]
  const linkId = evidenceLinkIds.get(citation.evidenceId)
  if (linkId && pageContext) {
    lines.push(showPageLink(pageContext, MODEL_TYPE.EVIDENCE, linkId))
  }
  if (citation.findingSummary) {
    lines.push(citation.findingSummary)
  }
  return lines.join('\n')
}

// The latest Operating Effectiveness test, one block per test procedure, mirroring the "copy
// summary and details" text the audit-scan UI produces so the csv reads the same way.
const formatTestDetails = (
  details: OeControlTestResult.AsObject[],
  pageContext: string,
  evidenceLinkIds: Map<string, string>,
): string =>
  details
    .map((result, index) => {
      const icon =
        result.determination === OeControlTestDetermination.PASS ? '✓' : '✗'
      let text = `${icon} ${index + 1}. ${result.testString}\n${
        result.reasoning
      }`
      if (result.citationsList.length) {
        const citationText = result.citationsList
          .map((citation) =>
            formatCitation(citation, pageContext, evidenceLinkIds),
          )
          .join('\n\n')
        text += `\n\nEvidence and Sources:\n${citationText}`
      }
      return text
    })
    .join('\n\n')

export type ReportExportFormat = 'csv' | 'xlsx'

export const buildControlsReportData = (
  controls: Control[],
  policies: Record<string, PolicyRecord>,
  controlPolicyNamesByControlId: Record<string, string[]>,
  controlDepartmentNamesByControlId: Record<string, string[]>,
  departments: Record<string, Department>,
  vendors: Record<string, VendorRecord>,
  frameworkGroups: Record<string, ComplianceFrameworkGroup>,
  risksMap: Record<string, Risk[]>,
  customStatusNamesById: Record<string, string>,
  controlTests?: Record<string, AuditReadinessTestRecord.AsObject[]>,
  pageContext = '',
  evidenceLinkIds: Map<string, string> = new Map(),
): { headers: string[]; rows: string[][] } => {
  const sortedCfNames = Object.keys(frameworkGroups).sort(
    (a: string, b: string) => a.localeCompare(b),
  )
  const testHeaders: Set<string> = controlTests
    ? Object.values(controlTests).reduce((set, list) => {
        list.forEach((test) => {
          set.add(nameResult(test.testName))
          if (pageContext) {
            set.add(nameUrl(test.testName))
          }
          set.add(nameReason(test.testName))
          set.add(nameDate(test.testName))
          set.add(nameAdvanced(test.testName))
          set.add(nameDetails(test.testName))
        })
        return set
      }, new Set<string>())
    : new Set<string>()

  const updatedHeaders = [
    ...withControlUrl(
      CONTROLS_CSV_HEADERS,
      pageContext ? CONTROL_URL_HEADER : undefined,
    ),
    ...sortedCfNames,
    ...Array.from(testHeaders),
  ]

  const data = controls.map((control: Control) => {
    const department = departments[control.getDepartmentId()]
    const frameworkIds = new Set(control.getFrameworkIdsList())
    const controlId = control.getMigratedControlId() || control.getId()
    const newControlId = control.getId().trim()
    const oldControlId = control.getMigratedControlId().trim()
    const policyNames = controlPolicyNamesByControlId[control.getId()] || []
    const policyNameForCsv =
      policyNames.length > 0
        ? policyNames.join('|')
        : policies[control.getPolicyModelId()]?.getName() || NONE_NAME
    const departmentNames =
      controlDepartmentNamesByControlId[control.getId()] || []
    const departmentNameForCsv =
      departmentNames.length > 0
        ? departmentNames.join('|')
        : department?.getName() || NONE_NAME

    const row = withControlUrl(
      [
        control.getModelId(),
        control.getName().trim(),
        control.getObjective().replace('##', '').trim(),
        control.getNote().trim(),
        (control.getProcedure() ?? '').trim(),
        policyNameForCsv,
        departmentNameForCsv,
        customStatusNamesById[control.getCustomStatusId()] ||
          controlStatusTitle[control.getStatus()],
        ResponsibilityOptions[control.getResponsibility()],
        control.getNotApplicableReason(),
        vendors[control.getVendorId()]?.getName() || '',
        control.getOwnerEmail(),
        control.getAssigneeEmailsList().join(','),
        formatSuggestedEvidence(control),
        formatTestProcedures(control),
        risksMap[newControlId]
          ?.map((risk) => `${risk.getCustomId()} ${risk.getName()}`)
          .join(',') ||
          risksMap[oldControlId]
            ?.map((risk) => `${risk.getCustomId()} ${risk.getName()}`)
            .join(',') ||
          '',
      ],
      pageContext
        ? showPageLink(pageContext, MODEL_TYPE.CONTROL, control.getModelId())
        : undefined,
    )

    const linkedCfbIds = new Set(control.getComplianceFrameworkIdsList())
    sortedCfNames.forEach((cfName: string) => {
      const group = frameworkGroups[cfName]
      const frameworks = group.objectives
        .filter((objective: Framework) =>
          frameworkIds.has(objective.getModelId()),
        )
        .map((objective: Framework) =>
          quoteRequirementForList(objective.getName()),
        )
        .join(',')
      if (frameworks) {
        row.push(frameworks)
      } else if (
        linkedCfbIds.has(group.base.getId()) ||
        linkedCfbIds.has(group.base.getModelId())
      ) {
        // Linked to the framework but no requirement links — emit the indicator
        // so the import re-creates the framework link with no requirements.
        row.push(NO_REQUIREMENTS_INDICATOR)
      } else {
        row.push('')
      }
    })
    const tests =
      controlTests && controlId in controlTests ? controlTests[controlId] : []

    // add columns for each test header
    Array.from(testHeaders).forEach((_) => row.push(''))

    tests.forEach((check) => {
      const resultIdx = updatedHeaders.findIndex(
        (elem) => elem === nameResult(check.testName),
      )
      const reasonIdx = updatedHeaders.findIndex(
        (elem) => elem === nameReason(check.testName),
      )
      const dateIdx = updatedHeaders.findIndex(
        (elem) => elem === nameDate(check.testName),
      )
      const advancedIdx = updatedHeaders.findIndex(
        (elem) => elem === nameAdvanced(check.testName),
      )
      const detailsIdx = updatedHeaders.findIndex(
        (elem) => elem === nameDetails(check.testName),
      )

      row[resultIdx] = getSingleCheckResultText(
        check.result,
        check.testName as CONTROL_TEST_NAMES,
      )
      row[reasonIdx] = getReasonText(check.reason)
      row[dateIdx] = getDateText(check.updatedAt)
      row[advancedIdx] = getAdvancedText(check.artifactId)
      row[detailsIdx] = formatTestDetails(
        check.detailsList,
        pageContext,
        evidenceLinkIds,
      )

      if (pageContext) {
        const urlIdx = updatedHeaders.findIndex(
          (elem) => elem === nameUrl(check.testName),
        )
        // A result carried over from a run that has since been deleted names no run to link to.
        row[urlIdx] = check.auditRunId
          ? showPageLink(pageContext, MODEL_TYPE.AUDITBOT_RUN, check.auditRunId)
          : ''
      }
    })

    return row
  })

  return { headers: updatedHeaders, rows: data }
}

export const generateControlsReport = async (
  controls: Control[],
  policies: Record<string, PolicyRecord>,
  controlPolicyNamesByControlId: Record<string, string[]>,
  controlDepartmentNamesByControlId: Record<string, string[]>,
  departments: Record<string, Department>,
  vendors: Record<string, VendorRecord>,
  frameworkGroups: Record<string, ComplianceFrameworkGroup>,
  risksMap: Record<string, Risk[]>,
  customStatusNamesById: Record<string, string>,
  controlTests?: Record<string, AuditReadinessTestRecord.AsObject[]>,
  pageContext = '',
  evidenceLinkIds: Map<string, string> = new Map(),
): Promise<Blob> => {
  const { headers, rows } = buildControlsReportData(
    controls,
    policies,
    controlPolicyNamesByControlId,
    controlDepartmentNamesByControlId,
    departments,
    vendors,
    frameworkGroups,
    risksMap,
    customStatusNamesById,
    controlTests,
    pageContext,
    evidenceLinkIds,
  )

  const guardedData = rows.map((row) =>
    row.map((cell) => guardFormulaCell(String(cell ?? ''))),
  )
  // Papa rather than a hand-joined header line: a compliance framework whose
  // NAME contains a comma used to add a header field and shift every column
  // after it.
  //
  // Quote guarded cells so that a reader that strips a leading spacefrom an unquoted
  // field won't silently undo the guard and bring the #NAME? error back
  // The header row needs guarding too: compliance-framework names come from user
  // input (a custom framework is validated only for being non-empty), so one named
  // "-Acme" would open as a formula. normalizeHeader trims on re-import.
  const csvRows = Papa.unparse(
    [headers.map(guardFormulaCell), ...guardedData],
    {
      quotes: (value) => /^\s/.test(String(value ?? '')),
      newline: '\r\n',
    },
  )
  // BOM so Excel decodes as UTF-8 instead of the system code page; without it
  // curly quotes and dashes arrive as mojibake. normalizeHeader strips it on
  // re-import.
  return new Blob(['\uFEFF' + csvRows], { type: 'text/csv;charset=utf-8;' })
}

export const DownloadControlsReport = async (
  controls: Controls | undefined,
  policies: PolicyRecords | undefined,
  controlPolicyNamesByControlId: Record<string, string[]>,
  controlDepartmentNamesByControlId: Record<string, string[]>,
  departments: Department[] | undefined,
  vendors: VendorRecord[] | undefined,
  complianceFrameworks: ComplianceFrameworks | undefined,
  cfObjectives: Frameworks | undefined,
  risks: Risk[] | undefined,
  controlStatuses: ControlStatusItem[] | undefined,
  controlCheckData?: AuditReadinessRecord[],
  // The account (and audit) the links belong to, from the route. Omitted by callers that build
  // links for nothing — without it the link columns and the citation links are left out.
  pageContext?: string,
  evidenceLinkIds?: Map<string, string>,
  format: ReportExportFormat = 'csv',
): Promise<void> => {
  const ctrls = controls?.getItemsList() || []
  const policiesMap = (policies?.getItemsList() || []).reduce(
    (acc: Record<string, PolicyRecord>, policy: PolicyRecord) => {
      acc[policy.getModelId()] = policy
      return acc
    },
    {},
  )
  const deptMap = (departments || []).reduce(
    (acc: Record<string, Department>, dept: Department) => {
      acc[dept.getId()] = dept
      acc[dept.getModelId()] = dept
      return acc
    },
    {},
  )

  const frameworkGroups = getFrameworkGroups(complianceFrameworks, cfObjectives)

  const controlTestsMap = (controlCheckData || []).reduce(
    (
      acc: Record<string, AuditReadinessTestRecord.AsObject[]>,
      record: AuditReadinessRecord,
    ) => {
      acc[record.getControlId()] = record
        .getControlTestsList()
        .map((ele) => ele.toObject())

      return acc
    },
    {},
  )

  const vendorsMap = (vendors || []).reduce(
    (acc: Record<string, VendorRecord>, vendor: VendorRecord) => {
      acc[vendor.getId()] = vendor
      return acc
    },
    {},
  )

  const risksMap = (risks || []).reduce(
    (acc: Record<string, Risk[]>, risk: Risk) => {
      risk
        .getControlIds()
        .split(',')
        .forEach((controlId) => {
          const trimmedControlId = controlId.trim()
          if (!acc[trimmedControlId]) {
            acc[trimmedControlId] = []
          }
          acc[trimmedControlId].push(risk)
        })
      return acc
    },
    {},
  )

  const customStatusNamesById = (controlStatuses || []).reduce(
    (acc: Record<string, string>, status: ControlStatusItem) => {
      acc[status.getId()] = status.getName()
      return acc
    },
    {},
  )

  const baseFileName = controlCheckData
    ? 'controls-with-checks'
    : 'control-detail'

  if (format === 'xlsx') {
    const { headers, rows } = buildControlsReportData(
      ctrls,
      policiesMap,
      controlPolicyNamesByControlId,
      controlDepartmentNamesByControlId,
      deptMap,
      vendorsMap,
      frameworkGroups,
      risksMap,
      customStatusNamesById,
      controlTestsMap,
      pageContext,
      evidenceLinkIds,
    )
    await exportToXlsx(`${baseFileName}.xlsx`, headers, rows, 'Controls')
    return
  }

  const csv = await generateControlsReport(
    ctrls,
    policiesMap,
    controlPolicyNamesByControlId,
    controlDepartmentNamesByControlId,
    deptMap,
    vendorsMap,
    frameworkGroups,
    risksMap,
    customStatusNamesById,
    controlTestsMap,
    pageContext,
    evidenceLinkIds,
  )
  await FileSaver.saveAs(csv, `${baseFileName}.csv`)
}
